import { GRID_PATTERN_IDS, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { ModalType } from '~/stores/modal'
import {
  getShapeSelectionIds,
  getShapeSelectionScope,
  hasValidDesignName,
} from '~/components/NewStudio/logic'

interface NewStudioActionOptions {
  onViewInRoom?: () => void | Promise<void>
}

export function useNewStudioActions(options: NewStudioActionOptions = {}) {
  const gridStore = useGridStore()
  const projectStore = useProjectStore()
  const userStore = useUserStore()
  const modalStore = useModalStore()
  const runtimeConfig = useRuntimeConfig()
  const showGridOptions = useState('new-studio-grid-options-open', () => false)

  const highlightedGridIds = useState<string[]>('new-studio-highlighted-grids', () => [])

  const hasShapes = computed(() => gridStore.shapeList.length > 0)
  const hasSelection = computed(() => gridStore.selectedItemsOnGrid.length > 0)
  const selectAllTargetLabel = computed(
    () => getShapeSelectionScope(gridStore.selectedItemsOnGrid)?.label || 'Shapes',
  )
  const hasUndo = computed(() => gridStore.undoStack.length > 0)
  const hasRedo = computed(() => gridStore.redoStack.length > 0)
  const hasDesign = computed(() => Boolean(projectStore.designId))
  const canArrange = computed(() => hasSelection.value && gridStore.gridPattern?.id !== GRID_PATTERN_IDS.THREE_D)
  const canAddToCart = computed(() => gridStore.getTilesList().length > 0 && !gridStore.isProcessing)

  const autoSave = () => {
    if (runtimeConfig.public.autoSaveOnChange) void projectStore.save(true, true)
  }

  const preview = () => navigateTo(`/preview/${projectStore.designId || 0}`)
  const startNew = () => modalStore.toggleModal(ModalType.New)
  const load = () => modalStore.toggleModal(ModalType.Load)
  const save = async () => {
    if (!userStore.isLoggedIn) return modalStore.toggleModal(ModalType.Login, { callback: save })
    if (!hasValidDesignName(projectStore.designName)) {
      return modalStore.toggleModal(ModalType.SaveAs, { options: { save: true } })
    }
    await projectStore.saveDesign()
  }
  const saveAs = () => {
    if (!userStore.isLoggedIn) return modalStore.toggleModal(ModalType.Login, { callback: saveAs })
    modalStore.toggleModal(ModalType.SaveAs, { options: { saveAs: true } })
  }
  const viewInRoom = () => options.onViewInRoom?.()
  const yourDesigns = () => {
    if (!userStore.isLoggedIn) return modalStore.toggleModal(ModalType.Login, { callback: yourDesigns })
    modalStore.toggleModal(ModalType.MyDesigns)
  }
  const settings = () => {
    if (!userStore.isLoggedIn) return modalStore.toggleModal(ModalType.Login, { callback: settings })
    modalStore.toggleModal(ModalType.SaveAs, { options: { settings: true } })
  }
  const share = () => {
    if (projectStore.designId) modalStore.toggleModal(ModalType.Share)
  }
  const login = () => userStore.isLoggedIn ? userStore.logout() : modalStore.toggleModal(ModalType.Login)
  const select = () => gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
  const lasso = () => gridStore.updateSelectedTool(SELECTABLE_TOOLS.LASSO)
  const undo = () => gridStore.undo()
  const redo = () => gridStore.redo()
  const deleteSelected = () => {
    if (!hasSelection.value) return
    gridStore.saveState()
    gridStore.deleteSelectedItemsOnGrid()
    autoSave()
  }
  const copy = async () => {
    if (!hasSelection.value) return
    gridStore.updateSelectedTool(SELECTABLE_TOOLS.COPY)
    await gridStore.copySelectedItems()
  }
  const cut = async () => {
    if (!hasSelection.value) return
    gridStore.updateSelectedTool(SELECTABLE_TOOLS.CUT)
    await gridStore.cutSelectedItems()
  }
  const toFront = () => {
    if (!canArrange.value) return
    gridStore.saveState()
    gridStore.moveItemsToFront(gridStore.selectedItemsOnGrid.map((item) => item.id))
    autoSave()
  }
  const toBack = () => {
    if (!canArrange.value) return
    gridStore.saveState()
    gridStore.moveItemsToBack(gridStore.selectedItemsOnGrid.map((item) => item.id))
    autoSave()
  }
  const gridOptions = (patternIds: string[] = []) => {
    highlightedGridIds.value = patternIds
    showGridOptions.value = true
  }
  const highlightAvailableGrids = (patternIds: string[]) => {
    if (showGridOptions.value) highlightedGridIds.value = patternIds
  }
  const pan = () => gridStore.updateSelectedTool(SELECTABLE_TOOLS.MOVE_GRID)
  const zoomIn = () => { gridStore.gridZoom = Math.abs(gridStore.gridZoom + 0.1) }
  const zoomOut = () => { gridStore.gridZoom = Math.max(0.1, Math.abs(gridStore.gridZoom - 0.1)) }
  const selectAll = () => {
    const ids = getShapeSelectionIds(
      gridStore.shapeList,
      gridStore.selectedItemsOnGrid,
    )
    gridStore.selectItemsOnGrid(ids)
    gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
  }
  const selectCurrentColor = () => {
    if (!gridStore.selectedColor) return
    const ids = gridStore.shapeList
      .filter((shape) => (shape.metadata || shape.attrs?.metadata)?.color_label === gridStore.selectedColor?.label)
      .map((shape) => shape.id)
    gridStore.selectItemsOnGrid(ids)
    gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
  }
  const deselectAll = () => gridStore.unselectItemsOnGrid()
  const viewTiles = () => modalStore.toggleModal(ModalType.MyTiles)
  const addToCart = () => projectStore.addToCart()
  const createProductAndAddToCart = () => projectStore.addToCart({ forceLegacyFlow: true })

  return {
    hasShapes, hasSelection, selectAllTargetLabel, hasUndo, hasRedo, hasDesign, canArrange, canAddToCart,
    preview, startNew, load, save, saveAs, viewInRoom, yourDesigns, settings, share, login,
    select, lasso, undo, redo, deleteSelected, copy, cut, toFront, toBack,
    showGridOptions, highlightedGridIds, gridOptions, highlightAvailableGrids, pan, zoomIn, zoomOut, selectAll, selectCurrentColor, deselectAll,
    viewTiles, addToCart, createProductAndAddToCart,
  }
}
